export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

export const formatMeasurement = (
  value: number | string | null | undefined,
  unit: string | null | undefined
): string => {
  if (!unit) return '';
  if (value === null || value === undefined) return '';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '';
  return `${numValue}${unit}`;
};

export const formatBearing = (
  value: number | string | null | undefined,
  normalize = false
): string => {
  if (value === null || value === undefined) return '';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '';

  if (normalize) {
    // Normalize to 0-360 range
    const normalizedBearing = ((numValue % 360) + 360) % 360;
    return formatMeasurement(normalizedBearing, '°');
  }

  return formatMeasurement(numValue, '°');
};

export const formatMagneticVariation = (
  magneticVariation: number | string | null | undefined
): string => {
  if (magneticVariation === null || magneticVariation === undefined) return '';

  const numValue =
    typeof magneticVariation === 'string'
      ? parseFloat(magneticVariation)
      : magneticVariation;

  if (isNaN(numValue)) return '';
  // Validate range (-180 to 180)
  if (numValue < -180 || numValue > 180) return '';

  const absVariation = Math.abs(numValue);
  const direction = numValue === 0 ? '' : numValue < 0 ? 'W' : 'E';
  return `${absVariation.toFixed(1)}°${direction ? ' ' + direction : ''}`;
};

export const formatAltitude = (
  value: number | string | null | undefined
): string => {
  if (value === null || value === undefined) return '';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '';
  // Validate reasonable altitude range (-1000 to 100000 feet)
  if (numValue < -1000 || numValue > 100000) return '';
  return formatMeasurement(numValue, 'ft');
};

export const formatString = (value?: string | null): string => {
  return value ?? '';
};

export const formatDatetime = (datetime: string | undefined): string => {
  if (!datetime) return '';

  try {
    // Parse the date, maintaining UTC by specifying the timezone as UTC
    const dateObject = datetime.endsWith('Z')
      ? new Date(datetime)
      : new Date(datetime + 'Z');

    if (isNaN(dateObject.getTime())) return '';

    const day = dateObject.getUTCDate().toString().padStart(2, '0');
    const month = MONTH_NAMES[dateObject.getUTCMonth()];
    const year = dateObject.getUTCFullYear();
    const hour = dateObject.getUTCHours().toString().padStart(2, '0');
    const minute = dateObject.getUTCMinutes().toString().padStart(2, '0');
    const second = dateObject.getUTCSeconds().toString().padStart(2, '0');

    return `${day} ${month} ${year} ${hour}:${minute}:${second}`;
  } catch {
    return '';
  }
};

export const formatLatLong = (latitude?: string | null, longitude?: string | null): string => {
  // Validate coordinate ranges
  if (
    typeof latitude !== "string" ||
    typeof longitude !== "string" ||
    latitude === "" ||
    longitude === "" ||
    latitude === null ||
    longitude === null ||
    latitude === undefined ||
    longitude === undefined
  ) {
    return "";
  } else {
    return `${latitude} / ${longitude}`;
  }
};

export const displayTimeZone = (timezoneString?: string | null): string => {
  const trimmed = timezoneString?.trim();
  if (!trimmed) return '';

  const [hours, minutes = '00'] = trimmed.split(':');
  if (!hours || isNaN(Number(hours))) return '';

  // Validate hours and minutes
  const numHours = Number(hours);
  const numMinutes = Number(minutes.padStart(2, '00'));

  if (numHours < -23 || numHours > 23) return '';
  if (isNaN(numMinutes) || numMinutes >= 60 || numMinutes < 0) return '';

  const offset =
    hours.startsWith('+') || hours.startsWith('-')
      ? `${hours}:${numMinutes.toString().padStart(2, '0')}`
      : `+${hours}:${numMinutes.toString().padStart(2, '0')}`;
  return `${offset} UTC`;
};
